import { Source } from "@models/source";
import { useSource, useSourceTypes } from "@stores/data.store";
import { useEffect, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import flatFileIcon from '@assets/images/connectors/flatfile.png';
import snowflakeIcon from '@assets/images/snowflake_logo.png';

interface SourceIconProps {
    source?: Source;
    sourceId?: string;
    className?: string;
    image_url?: string;
    compact?: boolean;
    showTitle?: boolean;
    title?: string;
}

const ImageContainer = styled.div<{ compact?: boolean }>`
    width: 50px;
    border-radius: 100%;
    overflow: hidden;

    cursor: pointer;

    img {
        height: 50px;
    }

    ${(props) =>
        props.compact &&
        css`
            width: 24px;
            img {
                height: 24px;
            }
        `}
`;

const SourceIcon = (props: SourceIconProps) => {
    
    const { isLoading: loadingSourceTypes, data: sourceTypeOptions } = useSourceTypes();

    // Some special sources (e.g. flat_files, snowflake) are virtual and not stored in Mongo, so
    // querying /sources/<id> would raise an InvalidId error on the backend.  Detect them and
    // disable the API call by passing an empty id to the hook.
    const isVirtualSource = props.sourceId === 'flat_files' || props.sourceId === 'snowflake';
    const sourceData = useSource(isVirtualSource ? "" : (props.sourceId || ""));

    const [theSource, setTheSource] = useState<Source | undefined>(undefined);

    useEffect(() => {
        if (props.source) {
            setTheSource(props.source);
        } else if (sourceData.dataUpdatedAt) {
            setTheSource(sourceData.data);
        }
    }, [props.sourceId, props.source, sourceData.dataUpdatedAt, sourceData.data]);

    const sourceName = useMemo(() => {
        if (props.title) {
            return props.title;
        }

        if (props.sourceId === 'flat_files') {
            return "Flat Files";
        }

        if (props.sourceId === 'snowflake') {
            return "Snowflake Table";
        }

        return theSource?.name || "";
    }, [props.title, props.sourceId, theSource?.name]);

    const renderImage = (src: string) => (
        <ImageContainer
            compact={props.compact}
            className={props.className}
            {...(props.showTitle && { title: sourceName })}
        >
            <img src={src} alt={sourceName || ""} />
        </ImageContainer>
    );

    if (props.sourceId === 'flat_files') {
        return renderImage(flatFileIcon);
    }

    if (props.sourceId === 'snowflake') {
        return renderImage(snowflakeIcon);
    }

    if (props.image_url) {
        return renderImage(props.image_url);
    }

    if (loadingSourceTypes || !theSource) {
        return null;
    }

    const sourceType = sourceTypeOptions?.find((st) => st.id === theSource?.type);

    if (!sourceType) {
        return null;
    }

    return renderImage(sourceType.icon_path);
};

export default SourceIcon;